import { OpenVerificationFormData, OpenVerificationContent } from 'cpnl-common';
import { CPDate, formatCpDate } from '@cp-shared-19/common-utilities';
import { CleaveInput, preventSubmit, ValidatedInput } from '@cp-shared-19/frontend-ui';
import { Button, ButtonContainer, Fieldset, Form, Layout } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { openVerificationValidationSchema } from './ValidationSchema';

type ChangeAddressProps = {
    handleSubmit: (data: OpenVerificationFormData) => void;
    openVerificationContent?: OpenVerificationContent;
};

type FormData = {
    contractNumber: string;
    licensePlate: string;
    initials: string;
    middleName?: string;
    lastName: string;
    birthday: CPDate;
};

export const OpenVerificationForm: React.FC<ChangeAddressProps> = ({ handleSubmit, openVerificationContent }) => {
    const { t } = useTranslation('open-verification');
    const content = openVerificationContent || {
        headline: '',
        description: '',
        contractNumber: '',
        contractNumberTooltip: '',
        licensePlate: '',
        initials: '',
        middleName: '',
        lastName: '',
        birthday: '',
        submit: '',
    };

    return (
        <>
            <Layout>
                <Layout.Item default={'1/1'} className={'u-mb u-text-center'}>
                    <h1>{content.headline}</h1>
                </Layout.Item>
                <Layout.Item className={'u-m-xxsmall'}>
                    <span dangerouslySetInnerHTML={{ __html: content.description }} />
                </Layout.Item>
                <Layout.Item>
                    <Formik
                        initialValues={{
                            contractNumber: '',
                            licensePlate: '',
                            initials: '',
                            middleName: '',
                            lastName: '',
                            birthday: '',
                        }}
                        validationSchema={openVerificationValidationSchema(t)}
                        onSubmit={(data: FormData) =>
                            handleSubmit({
                                ...data,
                                birthday: formatCpDate(data.birthday, 'DD.MM.YYYY', true).format('YYYY-MM-DD'),
                            })
                        }
                    >
                        {(formik) => (
                            <Form onSubmit={preventSubmit}>
                                <Fieldset>
                                    <Fieldset.Row>
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={content.contractNumber}
                                            tooltip={content.contractNumberTooltip}
                                            tooltipTitle="Contractnummer identificatie"
                                            name="contractNumber"
                                            testId="contractNumber"
                                            type="text"
                                            required
                                            autocomplete="on"
                                        />
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={content.licensePlate}
                                            name="licensePlate"
                                            testId="licensePlate"
                                            type="text"
                                            required
                                            autocomplete="on"
                                        />
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item default="1/2">
                                                <CleaveInput
                                                    className="c-form-field--no-floating"
                                                    label={content.initials}
                                                    name="initials"
                                                    testId="initials"
                                                    type="text"
                                                    autocomplete="on"
                                                    handleChange={(e: unknown) => {
                                                        formik.setFieldValue(
                                                            'initials',
                                                            (e as ChangeEvent<
                                                                HTMLInputElement
                                                            >).target.value.replaceAll(/[0-9]\./g, ''),
                                                        );
                                                    }}
                                                    cleaveOptions={{
                                                        delimiter: '.',
                                                        blocks: [1, 1, 1, 1, 1, 1, 1, 1, 1],
                                                        numericOnly: false,
                                                        uppercase: true,
                                                    }}
                                                    required
                                                />
                                            </Layout.Item>
                                            <Layout.Item default="1/2">
                                                <ValidatedInput
                                                    className="c-form-field--no-floating"
                                                    label={content.middleName}
                                                    name="middleName"
                                                    testId="middleName"
                                                    type="text"
                                                    autocomplete="additional-name"
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <ValidatedInput
                                            className="c-form-field--no-floating"
                                            label={content.lastName}
                                            name="lastName"
                                            testId="lastName"
                                            type="text"
                                            required
                                            autocomplete="family-name"
                                        />
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <CleaveInput
                                            className="c-form-field--no-floating"
                                            cleaveOptions={{
                                                delimiter: '.',
                                                blocks: [2, 2, 4],
                                                numericOnly: true,
                                            }}
                                            inputMode={'numeric'}
                                            label={content.birthday}
                                            name={'birthday'}
                                            testId={'date-of-birth'}
                                            autocomplete="bday"
                                            required
                                        />
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <ButtonContainer center className="u-mt">
                                            <Button
                                                onClick={formik.submitForm}
                                                testId={'submit-open-verification-button'}
                                                ariaLabel="Gegevens verzenden"
                                                title="Verzend gegevens"
                                                type="submit"
                                            >
                                                {content.submit}
                                            </Button>
                                        </ButtonContainer>
                                    </Fieldset.Row>
                                </Fieldset>
                            </Form>
                        )}
                    </Formik>
                </Layout.Item>
            </Layout>
        </>
    );
};
